import { useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { ApiError, get, put } from '../api/client';
import { isMissing } from '../api/queries';
import type { SensorCalibration } from './depthFilter';
import { settingsKey } from './charge';

const LS = (serial: string) => `cruzr.sensorCal.${serial}`;

function readLocal(serial: string): SensorCalibration | null {
  try {
    const v = localStorage.getItem(LS(serial));
    return v ? (JSON.parse(v) as SensorCalibration) : null;
  } catch {
    return null;
  }
}

/** Browser fallback copy of each robot's calibration (until the server stores `sensorCalibration`). */
const useLocalCal = create<{ bySerial: Record<string, SensorCalibration | null>; set: (serial: string, cal: SensorCalibration | null) => void }>((set) => ({
  bySerial: {},
  set: (serial, cal) =>
    set((s) => {
      try {
        if (cal) localStorage.setItem(LS(serial), JSON.stringify(cal));
        else localStorage.removeItem(LS(serial));
      } catch {
        /* storage blocked */
      }
      return { bySerial: { ...s.bySerial, [serial]: cal } };
    }),
}));

const valid = (c: unknown): c is SensorCalibration => Boolean(c && typeof c === 'object' && (c as SensorCalibration).v === 1 && Array.isArray((c as SensorCalibration).ignore));

/** The robot's sensor calibration: server `settings.sensorCalibration` wins, else the browser copy. */
export function useSensorCalibration(serial: string): SensorCalibration | null {
  const q = useQuery({
    queryKey: [...settingsKey(serial), 'raw'] as const,
    queryFn: () => get<{ sensorCalibration?: unknown } | null>(`/api/robots/${encodeURIComponent(serial)}/settings`),
    retry: (n, e) => !isMissing(e) && n < 1,
    staleTime: 60_000,
    enabled: Boolean(serial),
  });
  const local = useLocalCal((s) => (serial in s.bySerial ? s.bySerial[serial] : undefined));
  const server = q.data?.sensorCalibration;
  if (valid(server)) return server;
  return local !== undefined ? local : readLocal(serial);
}

/** Save: PUT settings {sensorCalibration}; if the server doesn't know the field yet, keep it in this browser. */
export function useSaveCalibration(serial: string) {
  const qc = useQueryClient();
  const setLocal = useLocalCal((s) => s.set);
  return async (cal: SensorCalibration | null): Promise<'server' | 'local'> => {
    setLocal(serial, cal);
    try {
      const r = await put<{ sensorCalibration?: unknown } | null>(`/api/robots/${encodeURIComponent(serial)}/settings`, { sensorCalibration: cal });
      await qc.invalidateQueries({ queryKey: settingsKey(serial) });
      return valid(r?.sensorCalibration) || (cal === null && r) ? 'server' : 'local';
    } catch (e) {
      if (e instanceof ApiError) return 'local';
      throw e;
    }
  };
}
